import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { useContent } from "@/content/ContentContext";

const Navbar = () => {
  const { content } = useContent();
  const { site } = content;
  const LINKS = [
    { to: "/", label: site.nav.home, id: "home" },
    { to: "/about", label: site.nav.about, id: "about" },
    { to: "/services", label: site.nav.services, id: "services" },
    { to: "/training", label: site.nav.training, id: "training" },
    { to: "/contact", label: site.nav.contact, id: "contact" },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
          scrolled
            ? "bg-cream/85 backdrop-blur-xl border-b border-line"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-navbar"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              className="flex items-center gap-3 group"
              data-testid="nav-logo-link"
              aria-label={`${site.brandName} ${site.brandSub} — Home`}
            >
              <LogoMark size={42} dark={!scrolled} className="transition-transform duration-500 group-hover:rotate-[8deg]" />
              <span className="leading-tight">
                <span className={`block font-serif text-lg font-semibold tracking-wide transition-colors duration-500 ${scrolled ? "text-pine" : "text-cream"}`}>
                  {site.brandName}
                </span>
                <span className={`block text-[10px] font-semibold tracking-[0.28em] uppercase transition-colors duration-500 ${scrolled ? "text-gold-muted" : "text-gold"}`}>
                  {site.brandSub}
                </span>
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-link-${l.id}`}
                  className={({ isActive }) =>
                    `relative text-sm font-medium tracking-wide transition-colors duration-300 link-underline-gold pb-1 ${
                      scrolled
                        ? isActive
                          ? "text-pine"
                          : "text-ink/60 hover:text-pine"
                        : isActive
                          ? "text-cream"
                          : "text-cream/65 hover:text-cream"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <Link
                to="/contact"
                data-testid="nav-cta-get-in-touch"
                className={`hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  scrolled
                    ? "bg-pine text-cream hover:bg-gold hover:text-pine"
                    : "bg-gold text-pine hover:bg-cream"
                }`}
              >
                {site.ctaPrimary}
                <ArrowUpRight size={15} strokeWidth={2.2} />
              </Link>
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="nav-mobile-menu-button"
                aria-label="Open menu"
                className={`lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border backdrop-blur transition-colors duration-500 ${
                  scrolled ? "border-line bg-cream/70 text-pine" : "border-cream/30 bg-cream/10 text-cream"
                }`}
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-pine grain flex flex-col"
            data-testid="nav-mobile-menu"
          >
            <div className="relative z-10 flex items-center justify-between h-20 px-5 sm:px-8">
              <div className="flex items-center gap-3">
                <LogoMark size={42} dark />
                <span className="leading-tight">
                  <span className="block font-serif text-lg font-semibold tracking-wide text-cream">
                    {site.brandName}
                  </span>
                  <span className="block text-[10px] font-semibold tracking-[0.28em] uppercase text-gold">
                    {site.brandSub}
                  </span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-close-button"
                aria-label="Close menu"
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-cream/25 text-cream"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="relative z-10 flex flex-1 flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className={({ isActive }) =>
                      `block font-serif text-4xl sm:text-5xl py-2 transition-colors duration-300 ${
                        isActive ? "text-gold italic" : "text-cream hover:text-gold"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="mt-10 text-cream/60 text-sm tracking-wide"
              >
                <a href={`tel:${site.phoneHref}`} data-testid="nav-mobile-phone-link" className="block py-1 text-cream text-lg font-medium">
                  {site.phone}
                </a>
                <a href={`mailto:${site.email}`} data-testid="nav-mobile-email-link" className="block py-1">
                  {site.email}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
